import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Plus, Edit, Trash2, Send, CheckCircle2,
  XCircle, AlertTriangle, Download, X, Save, UserPlus,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  DRAFT: "bg-gray-100 text-gray-700",
  PENDING_APPROVAL: "bg-yellow-100 text-yellow-800",
  ACTIVE: "bg-emerald-100 text-emerald-700",
  EXPIRED: "bg-red-100 text-red-700",
};
const DIVISOR_OPTIONS = [2500, 3000, 3500, 4000, 4500, 5000];
const DIVISOR_DEFAULTS = { B2B: 4000, B2C: 5000, C2C: 5000 };
const CUSTOMER_TYPES = ["B2B Bulk", "B2C Bulk", "Regular Customer", "Retail/Walk-in"];
const ZONE_LABELS = [
  { code: "A",  label: "Within City (0-50km) Zone A" },
  { code: "B",  label: "Single Line (51-400km) Zone B" },
  { code: "D1", label: "Zone D1 (401-1000km)" },
  { code: "D2", label: "Zone D2 (1001-1500km)" },
  { code: "E",  label: "Special Zone E (1501-2000km)" },
  { code: "F",  label: "Special Zone F (2000km+)" },
  { code: "C1", label: "Metro Zone C1 (401-1000km)" },
  { code: "C2", label: "Metro Zone C2 (1000km+)" },
];
const SLAB_HEADERS = ["0.25kg", "0.5kg", "1kg", "Per 0.5kg (1-5kg)", "Per kg (5-10kg)", "Per kg (10-20kg)"];
const DEFAULT_ZONE_RATES = {
  A: [50,80,100,50,100,90], B: [80,100,120,60,110,100],
  D1: [85,105,130,65,120,110], D2: [90,110,140,70,130,120],
  E: [105,125,160,80,140,130], F: [110,130,180,85,150,140],
  C1: [95,115,145,75,140,130], C2: [100,120,150,80,130,120],
};
const B2B_ZONE_PAIRS = [
  { code: "S1-S1", label: "S1 → S1" }, { code: "S1-S2", label: "S1 → S2" },
  { code: "S1-N",  label: "S1 → North" }, { code: "S1-E",  label: "S1 → East" },
  { code: "S1-W",  label: "S1 → West" }, { code: "S1-C",  label: "S1 → Central" },
  { code: "N-N",   label: "North → North" }, { code: "PAN",   label: "Pan India" },
];

/* ─── Editable Zone Grid (8 zones × 6 slabs) ─── */
function ZoneRateGrid({ rates, onChange }) {
  const update = (zone, col, val) => {
    const next = { ...rates };
    next[zone] = [...(next[zone] || [0,0,0,0,0,0])];
    next[zone][col] = parseFloat(val) || 0;
    onChange(next);
  };
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead className="bg-slate-100">
          <tr>
            <th className="px-2 py-1.5 text-left text-[10px] font-bold text-slate-500 w-48">Zone</th>
            {SLAB_HEADERS.map(h => <th key={h} className="px-1 py-1.5 text-center text-[10px] font-bold text-slate-500 w-20">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y">
          {ZONE_LABELS.map(z => (
            <tr key={z.code} className="hover:bg-slate-50">
              <td className="px-2 py-1 text-xs text-slate-700 font-medium">{z.label}</td>
              {[0,1,2,3,4,5].map(col => (
                <td key={col} className="px-0.5 py-0.5">
                  <input type="number" min="0" step="1"
                    className="w-full border rounded px-1.5 py-1 text-xs text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={rates?.[z.code]?.[col] ?? ""}
                    onChange={e => update(z.code, col, e.target.value)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── B2B Zone Pair Rate Grid ─── */
function B2BZonePairGrid({ rates, onChange }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-xs">
        <thead className="bg-slate-100">
          <tr><th className="px-3 py-1.5 text-left text-[10px] font-bold text-slate-500">Zone Pair</th><th className="px-3 py-1.5 text-center text-[10px] font-bold text-slate-500 w-32">Rate per Kg (₹)</th></tr>
        </thead>
        <tbody className="divide-y">
          {B2B_ZONE_PAIRS.map(z => (
            <tr key={z.code} className="hover:bg-slate-50">
              <td className="px-3 py-1 text-xs text-slate-700 font-medium">{z.label}</td>
              <td className="px-1 py-0.5">
                <input type="number" min="0" step="0.5"
                  className="w-full border rounded px-2 py-1 text-xs text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={rates?.[z.code] ?? ""}
                  onChange={e => { const n={...rates}; n[z.code]=parseFloat(e.target.value)||0; onChange(n); }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Insurance Radio (mandatory, nothing pre-selected) ─── */
function InsuranceRadio({ value, onChange, error, networkType }) {
  // value = { type: "CARRIER_RISK"|"OWNER_RISK"|"", rate_percent: float, min_amount: float }
  const ins = value || {};
  const isOwn = networkType === "OWN";

  const DEFAULTS = {
    CARRIER_RISK: { rate_percent: isOwn ? 0.5 : 0.4, min_amount: isOwn ? 50 : 150 },
    OWNER_RISK:   { rate_percent: isOwn ? 0 : 0.1,   min_amount: 0 },
  };

  const selectType = (t) => {
    const d = DEFAULTS[t];
    onChange({ type: t, rate_percent: d.rate_percent, min_amount: d.min_amount });
  };

  const opts = [
    { val: "CARRIER_RISK", label: isOwn ? "Carrier Risk (default 0.5%, min ₹50)" : "Carrier Risk (default 0.4%, min ₹150)" },
    { val: "OWNER_RISK",   label: isOwn ? "Owner Risk (Nil)" : "Owner Risk (default 0.1%)" },
  ];

  return (
    <div>
      <label className="text-xs font-medium text-slate-600 block mb-1">Insurance <span className="text-red-500">*</span></label>
      <div className="space-y-2">
        {opts.map(o => (
          <div key={o.val}>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name={`ins_${networkType}`} checked={ins.type===o.val} onChange={()=>selectType(o.val)} className="accent-indigo-600" />
              <span className="text-sm text-slate-700">{o.label}</span>
            </label>
            {ins.type===o.val && (
              <div className="ml-6 mt-1.5 flex gap-3">
                <div>
                  <label className="text-[10px] text-slate-500">Rate (%)</label>
                  <input type="number" step="0.01" min="0"
                    className="w-20 border rounded px-2 py-1 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={ins.rate_percent ?? ""} onChange={e=>onChange({...ins, rate_percent: parseFloat(e.target.value)||0})} />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">Min Amount (₹)</label>
                  <input type="number" step="1" min="0"
                    className="w-24 border rounded px-2 py-1 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={ins.min_amount ?? ""} onChange={e=>onChange({...ins, min_amount: parseFloat(e.target.value)||0})} />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">Please select an insurance type</p>}
    </div>
  );
}

/* ─── Create Customer Inner Modal ─── */
function CreateCustomerModal({ open, onClose, onCreated, headers }) {
  const [f, setF] = useState({ name:"", business_name:"", email:"", phone:"", customer_type:"B2B Bulk", gstin:"", pan:"", address:"", city:"", state:"", pincode:"", credit_limit:0, wallet_preload:0, role:"customer", password:"Welcome@123" });
  const [saving, setSaving] = useState(false);
  const u = (k,v) => setF(p=>({...p,[k]:v}));
  const save = async () => {
    if (!f.business_name||!f.name||!f.email||!f.phone) return toast.error("Fill all required fields");
    setSaving(true);
    const r = await fetch(`${API}/api/users`, { method:"POST", headers, body: JSON.stringify({...f, name: f.name || f.business_name}) });
    if (r.ok) { const d=await r.json(); toast.success("Customer created"); onCreated(d); onClose(); }
    else { const e=await r.json(); toast.error(e.detail||"Failed to create customer"); }
    setSaving(false);
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-white overflow-y-auto" data-testid="create-customer-modal">
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><UserPlus className="w-5 h-5 text-indigo-600"/>Create New Customer</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5"/></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs font-medium text-slate-600">Business Name <span className="text-red-500">*</span></label><Input value={f.business_name} onChange={e=>u("business_name",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Owner/Contact Name <span className="text-red-500">*</span></label><Input value={f.name} onChange={e=>u("name",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Email <span className="text-red-500">*</span></label><Input type="email" value={f.email} onChange={e=>u("email",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Phone <span className="text-red-500">*</span></label><Input value={f.phone} onChange={e=>u("phone",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Customer Type</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={f.customer_type} onChange={e=>u("customer_type",e.target.value)}>
              {CUSTOMER_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select></div>
          <div><label className="text-xs font-medium text-slate-600">GSTIN</label><Input value={f.gstin} onChange={e=>u("gstin",e.target.value)} className="mt-1" placeholder="Optional"/></div>
          <div><label className="text-xs font-medium text-slate-600">PAN</label><Input value={f.pan} onChange={e=>u("pan",e.target.value)} className="mt-1" placeholder="Optional"/></div>
          <div><label className="text-xs font-medium text-slate-600">Billing Address <span className="text-red-500">*</span></label><Input value={f.address} onChange={e=>u("address",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">City <span className="text-red-500">*</span></label><Input value={f.city} onChange={e=>u("city",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">State <span className="text-red-500">*</span></label><Input value={f.state} onChange={e=>u("state",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Pincode <span className="text-red-500">*</span></label><Input value={f.pincode} onChange={e=>u("pincode",e.target.value)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Credit Limit (₹)</label><Input type="number" value={f.credit_limit} onChange={e=>u("credit_limit",parseFloat(e.target.value)||0)} className="mt-1"/></div>
          <div><label className="text-xs font-medium text-slate-600">Wallet Pre-load (₹)</label><Input type="number" value={f.wallet_preload} onChange={e=>u("wallet_preload",parseFloat(e.target.value)||0)} className="mt-1"/></div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">{saving?"Creating...":"Create Customer"}</Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FULL SCREEN AGREEMENT FORM
   ═══════════════════════════════════════════════════════════════ */
function AgreementFormModal({ open, onClose, editData, customers, onSaved, headers }) {
  const isEdit = !!editData;
  const [showNewCust, setShowNewCust] = useState(false);
  const [insError, setInsError] = useState(false);

  const [form, setForm] = useState({
    entity_type: "CUSTOMER", entity_id: "", entity_name: "", customer_type: "B2B Bulk",
    network_type: "OWN", service_type: "EXPRESS", order_type: "B2B",
    valid_from: "", valid_to: "", notes: "",
    rate_overrides: { volumetric_divisor: 4000, margin_percent: 20, zone_rates: {} },
  });

  useEffect(() => {
    if (editData) {
      setForm({
        entity_type: editData.entity_type || "CUSTOMER",
        entity_id: editData.entity_id || "",
        entity_name: editData.entity_name || "",
        customer_type: editData.customer_type || "B2B Bulk",
        network_type: editData.network_type || "OWN",
        service_type: editData.service_type || "EXPRESS",
        order_type: editData.order_type || "B2B",
        valid_from: editData.valid_from || "",
        valid_to: editData.valid_to || "",
        notes: editData.notes || "",
        rate_overrides: editData.rate_overrides || { volumetric_divisor: 4000, margin_percent: 20 },
      });
    } else {
      setForm({
        entity_type: "CUSTOMER", entity_id: "", entity_name: "", customer_type: "B2B Bulk",
        network_type: "OWN", service_type: "EXPRESS", order_type: "B2B",
        valid_from: "", valid_to: "", notes: "",
        rate_overrides: { volumetric_divisor: 4000, margin_percent: 20, zone_rates: {} },
      });
    }
    setInsError(false);
  }, [editData, open]);

  const u = (k, v) => {
    setForm(p => {
      const n = { ...p, [k]: v };
      if (k === "order_type") n.rate_overrides = { ...p.rate_overrides, volumetric_divisor: DIVISOR_DEFAULTS[v] || 4000 };
      return n;
    });
  };
  const uo = (k, v) => setForm(p => ({ ...p, rate_overrides: { ...p.rate_overrides, [k]: v } }));

  const handleCustomerSelect = (val) => {
    if (val === "__NEW__") { setShowNewCust(true); return; }
    const c = customers.find(c => c.id === val);
    u("entity_id", val);
    u("entity_name", c?.name || c?.business_name || "");
  };

  const onCustomerCreated = (newCust) => {
    u("entity_id", newCust.id || "");
    u("entity_name", newCust.name || newCust.business_name || "");
  };

  const validate = () => {
    if (!form.entity_id) { toast.error("Select a customer"); return false; }
    if (!form.valid_from || !form.valid_to) { toast.error("Set validity dates"); return false; }
    if (form.valid_from >= form.valid_to) { toast.error("Valid From must be before Valid To"); return false; }
    if (!form.rate_overrides.insurance?.type) { setInsError(true); toast.error("Select insurance type"); return false; }
    setInsError(false);
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    if (isEdit) {
      const r = await fetch(`${API}/api/v2/rate-agreements/${editData.id}`, {
        method: "PUT", headers, body: JSON.stringify({ rate_overrides: form.rate_overrides, valid_from: form.valid_from, valid_to: form.valid_to, notes: form.notes }),
      });
      if (r.ok) { toast.success("Agreement updated"); onSaved(); onClose(); }
      else { const e = await r.json(); toast.error(e.detail || "Update failed"); }
    } else {
      const r = await fetch(`${API}/api/v2/rate-agreements`, {
        method: "POST", headers, body: JSON.stringify(form),
      });
      if (r.ok) { toast.success("Agreement saved as draft"); onSaved(); onClose(); }
      else { const e = await r.json(); toast.error(e.detail || "Create failed"); }
    }
  };

  const isOwn = form.network_type === "OWN";
  const isB2B = form.order_type === "B2B";
  const is3PLB2B = form.network_type === "3PL_B2B";
  const is3PLB2C = form.network_type === "3PL_B2C";
  const showZoneGrid = (isOwn && !isB2B) || is3PLB2C;
  const showB2BZonePairs = is3PLB2B;
  const is3PL = is3PLB2B || is3PLB2C;

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-testid="agreement-form-modal">
      <CreateCustomerModal open={showNewCust} onClose={()=>setShowNewCust(false)} onCreated={onCustomerCreated} headers={headers} />
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">{isEdit ? "Edit Agreement" : "New Rate Agreement"}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
      </div>
      {/* Two-column body */}
      <div className="max-w-7xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Customer & Agreement Info */}
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-slate-800 border-b pb-2">Customer & Agreement Info</h3>
          <div>
            <label className="text-xs font-medium text-slate-600">Customer Type <span className="text-red-500">*</span></label>
            <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.customer_type} onChange={e=>u("customer_type",e.target.value)} data-testid="select-customer-type">
              {CUSTOMER_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Customer <span className="text-red-500">*</span></label>
            <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.entity_id} onChange={e=>handleCustomerSelect(e.target.value)} data-testid="select-customer">
              <option value="">Select Customer...</option>
              {customers.map(c=><option key={c.id} value={c.id}>{c.name||c.email} {c.customer_type?`(${c.customer_type})`:""}</option>)}
              <option value="__NEW__">+ Create New Customer</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Network <span className="text-red-500">*</span></label>
              <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.network_type} onChange={e=>u("network_type",e.target.value)} data-testid="select-network">
                <option value="OWN">OWN</option><option value="3PL_B2B">3PL B2B</option><option value="3PL_B2C">3PL B2C</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Service <span className="text-red-500">*</span></label>
              <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.service_type} onChange={e=>u("service_type",e.target.value)}>
                <option value="EXPRESS">Express</option><option value="SURFACE">Surface</option><option value="STANDARD">Standard</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Order Type <span className="text-red-500">*</span></label>
              <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.order_type} onChange={e=>u("order_type",e.target.value)} data-testid="select-order-type">
                <option value="B2B">B2B</option><option value="B2C">B2C</option><option value="C2C">C2C</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600">Valid From <span className="text-red-500">*</span></label><Input type="date" value={form.valid_from} onChange={e=>u("valid_from",e.target.value)} className="mt-1"/></div>
            <div><label className="text-xs font-medium text-slate-600">Valid To <span className="text-red-500">*</span></label><Input type="date" value={form.valid_to} onChange={e=>u("valid_to",e.target.value)} className="mt-1"/></div>
          </div>
          <div><label className="text-xs font-medium text-slate-600">Notes</label><Textarea value={form.notes} onChange={e=>u("notes",e.target.value)} placeholder="Optional notes..." className="mt-1" rows={2}/></div>
        </div>

        {/* RIGHT: Rate Overrides */}
        <div className="space-y-4">
          {/* ── OWN + B2B ── */}
          {isOwn && isB2B && (<>
            <h3 className="text-sm font-bold text-slate-800 border-b pb-2">Own Network B2B Rate Structure</h3>
            <div>
              <label className="text-xs font-medium text-slate-600">Volumetric Divisor</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.rate_overrides.volumetric_divisor||4000} onChange={e=>uo("volumetric_divisor",parseInt(e.target.value))}>
                {DIVISOR_OPTIONS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
              <p className="text-[10px] text-slate-400 mt-0.5">Higher = lower volumetric weight = customer-friendly</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-600">Base Rate/Kg (₹) <span className="text-red-500">*</span></label><Input type="number" placeholder="₹10/kg" value={form.rate_overrides.base_rate_per_kg||""} onChange={e=>uo("base_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">LR / Processing (₹)</label><Input type="number" placeholder="₹50" value={form.rate_overrides.lr_charge??50} onChange={e=>uo("lr_charge",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">FM Rate/Kg (₹)</label><Input type="number" placeholder="₹2/kg" value={form.rate_overrides.fm_rate_per_kg??2} onChange={e=>uo("fm_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">LM Rate/Kg (₹)</label><Input type="number" placeholder="₹2/kg" value={form.rate_overrides.lm_rate_per_kg??2} onChange={e=>uo("lm_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Midmile Rate/Kg (₹)</label><Input type="number" placeholder="₹10/kg" value={form.rate_overrides.midmile_rate_per_kg??10} onChange={e=>uo("midmile_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Fuel Surcharge (%)</label><Input type="number" placeholder="20%" value={form.rate_overrides.fuel_surcharge_percent??20} onChange={e=>uo("fuel_surcharge_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Min Chargeable Wt (kg)</label><Input type="number" placeholder="10" value={form.rate_overrides.min_chargeable_weight??10} onChange={e=>uo("min_chargeable_weight",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Margin (%) <span className="text-red-500">*</span></label><Input type="number" placeholder="20%" value={form.rate_overrides.margin_percent||""} onChange={e=>uo("margin_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            </div>
            <InsuranceRadio value={form.rate_overrides.insurance} onChange={v=>uo("insurance",v)} error={insError} networkType="OWN" />
          </>)}

          {/* ── OWN + B2C/C2C ── */}
          {isOwn && !isB2B && (<>
            <h3 className="text-sm font-bold text-slate-800 border-b pb-2">Own Network B2C Rate Structure</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600">Volumetric Divisor</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.rate_overrides.volumetric_divisor||5000} onChange={e=>uo("volumetric_divisor",parseInt(e.target.value))}>
                  {DIVISOR_OPTIONS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-600">Min Chargeable Wt (kg)</label><Input type="number" placeholder="0.25" value={form.rate_overrides.min_chargeable_weight??0.25} onChange={e=>uo("min_chargeable_weight",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Margin (%) <span className="text-red-500">*</span></label><Input type="number" placeholder="20%" value={form.rate_overrides.margin_percent||""} onChange={e=>uo("margin_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            </div>
            <InsuranceRadio value={form.rate_overrides.insurance} onChange={v=>uo("insurance",v)} error={insError} networkType="OWN" />
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Zone Rate Table (₹, exclusive of GST)</label>
              <ZoneRateGrid rates={form.rate_overrides.zone_rates||{}} onChange={v=>uo("zone_rates",v)} />
            </div>
          </>)}

          {/* ── 3PL B2B ── */}
          {is3PLB2B && (<>
            <h3 className="text-sm font-bold text-slate-800 border-b pb-2">3PL B2B Rate Structure</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Vendor <span className="text-red-500">*</span></label>
                <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.rate_overrides.vendor_code||""} onChange={e=>uo("vendor_code",e.target.value)}>
                  <option value="">Select Vendor</option><option value="DELHIVERY">Delhivery</option><option value="BLUEDART">BlueDart</option><option value="INDIAPOST">India Post</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Volumetric Divisor</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.rate_overrides.volumetric_divisor||4500} onChange={e=>uo("volumetric_divisor",parseInt(e.target.value))}>
                  {DIVISOR_OPTIONS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-600">LR / Processing (₹)</label><Input type="number" placeholder="₹250" value={form.rate_overrides.lr_charge??250} onChange={e=>uo("lr_charge",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">FM Rate/Kg (₹)</label><Input type="number" placeholder="₹1/kg" value={form.rate_overrides.fm_rate_per_kg??1} onChange={e=>uo("fm_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Fuel Surcharge (%)</label><Input type="number" placeholder="20%" value={form.rate_overrides.fuel_surcharge_percent??20} onChange={e=>uo("fuel_surcharge_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Min Chargeable Wt (kg)</label><Input type="number" placeholder="20" value={form.rate_overrides.min_chargeable_weight??20} onChange={e=>uo("min_chargeable_weight",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div className="col-span-2"><label className="text-xs text-slate-600">Margin (%) <span className="text-red-500">*</span></label><Input type="number" placeholder="20%" value={form.rate_overrides.margin_percent||""} onChange={e=>uo("margin_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            </div>
            <InsuranceRadio value={form.rate_overrides.insurance} onChange={v=>uo("insurance",v)} error={insError} networkType="3PL_B2B" />
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Rate per Kg by Zone Pair (₹)</label>
              <B2BZonePairGrid rates={form.rate_overrides.zone_rates||{}} onChange={v=>uo("zone_rates",v)} />
            </div>
          </>)}

          {/* ── 3PL B2C ── */}
          {is3PLB2C && (<>
            <h3 className="text-sm font-bold text-slate-800 border-b pb-2">3PL B2C Rate Structure</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Vendor <span className="text-red-500">*</span></label>
                <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.rate_overrides.vendor_code||""} onChange={e=>uo("vendor_code",e.target.value)}>
                  <option value="">Select Vendor</option><option value="DELHIVERY">Delhivery</option><option value="BLUEDART">BlueDart</option><option value="INDIAPOST">India Post</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Volumetric Divisor</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.rate_overrides.volumetric_divisor||5000} onChange={e=>uo("volumetric_divisor",parseInt(e.target.value))}>
                  {DIVISOR_OPTIONS.map(d=><option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-600">Min Chargeable Wt (kg)</label><Input type="number" placeholder="0.25" value={form.rate_overrides.min_chargeable_weight??0.25} onChange={e=>uo("min_chargeable_weight",parseFloat(e.target.value)||0)} className="mt-1"/></div>
              <div><label className="text-xs text-slate-600">Margin (%) <span className="text-red-500">*</span></label><Input type="number" placeholder="20%" value={form.rate_overrides.margin_percent||""} onChange={e=>uo("margin_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            </div>
            <InsuranceRadio value={form.rate_overrides.insurance} onChange={v=>uo("insurance",v)} error={insError} networkType="3PL_B2C" />
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Zone Rate Table (₹, exclusive of GST)</label>
              <ZoneRateGrid rates={form.rate_overrides.zone_rates||{}} onChange={v=>uo("zone_rates",v)} />
            </div>
          </>)}
        </div>
      </div>
      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex items-center justify-between">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-agreement-btn"><Save className="w-4 h-4 mr-1"/>{isEdit?"Update Agreement":"Save as Draft"}</Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   RETAIL RATE CARD TAB
   ═══════════════════════════════════════════════════════════════ */
function RetailRateCard({ headers }) {
  const [b2cRates, setB2cRates] = useState({...DEFAULT_ZONE_RATES});
  const [b2bOverrides, setB2bOverrides] = useState({ volumetric_divisor:4000, margin_percent:20, base_rate_per_kg:10, lr_charge:50, fm_rate_per_kg:2, lm_rate_per_kg:2, midmile_rate_per_kg:10, fuel_surcharge_percent:20, min_chargeable_weight:10, insurance:{} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load existing retail rates
    fetch(`${API}/api/v2/rate-agreements/entity/RETAIL_UNIVERSAL/RETAIL_B2C`, { headers }).then(r=>r.json()).then(d => {
      if (d.agreement?.rate_overrides?.zone_rates) setB2cRates(d.agreement.rate_overrides.zone_rates);
    });
    fetch(`${API}/api/v2/rate-agreements/entity/RETAIL_UNIVERSAL/RETAIL_B2B`, { headers }).then(r=>r.json()).then(d => {
      if (d.agreement?.rate_overrides) setB2bOverrides(prev => ({...prev, ...d.agreement.rate_overrides}));
    });
  }, []);

  const saveB2C = async () => {
    setSaving(true);
    const r = await fetch(`${API}/api/v2/rate-agreements`, { method:"POST", headers, body: JSON.stringify({
      entity_type:"RETAIL_UNIVERSAL", entity_id:"RETAIL_B2C", entity_name:"Retail B2C Universal",
      network_type:"3PL_B2C", service_type:"STANDARD", order_type:"B2C",
      rate_overrides:{zone_rates:b2cRates}, valid_from:"2026-01-01", valid_to:"2099-12-31",
    })});
    if (r.ok) toast.success("B2C Retail rates saved");
    else toast.error("Failed to save B2C rates");
    setSaving(false);
  };

  const saveB2B = async () => {
    setSaving(true);
    const r = await fetch(`${API}/api/v2/rate-agreements`, { method:"POST", headers, body: JSON.stringify({
      entity_type:"RETAIL_UNIVERSAL", entity_id:"RETAIL_B2B", entity_name:"Retail B2B Universal",
      network_type:"OWN", service_type:"STANDARD", order_type:"B2B",
      rate_overrides:b2bOverrides, valid_from:"2026-01-01", valid_to:"2099-12-31",
    })});
    if (r.ok) toast.success("B2B Walk-in rates saved");
    else toast.error("Failed to save B2B rates");
    setSaving(false);
  };

  const ub = (k,v) => setB2bOverrides(p=>({...p,[k]:v}));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Universal Retail Rate Card</h2>
        <p className="text-xs text-slate-500">Applies automatically to all Retail/Walk-in customers</p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* LEFT: B2C */}
        <div className="border rounded-xl p-4 space-y-4">
          <h3 className="text-sm font-bold text-blue-700">B2C / Walk-in Rates</h3>
          <ZoneRateGrid rates={b2cRates} onChange={setB2cRates} />
          <Button onClick={saveB2C} disabled={saving} className="bg-blue-600 hover:bg-blue-700"><Save className="w-4 h-4 mr-1"/>Save B2C Retail Rates</Button>
        </div>
        {/* RIGHT: B2B Walk-in */}
        <div className="border rounded-xl p-4 space-y-4">
          <h3 className="text-sm font-bold text-purple-700">B2B Walk-in Rates</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Volumetric Divisor</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={b2bOverrides.volumetric_divisor} onChange={e=>ub("volumetric_divisor",parseInt(e.target.value))}>
                {DIVISOR_OPTIONS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-slate-600">Base Rate/Kg (₹)</label><Input type="number" value={b2bOverrides.base_rate_per_kg} onChange={e=>ub("base_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">LR Charge (₹)</label><Input type="number" value={b2bOverrides.lr_charge} onChange={e=>ub("lr_charge",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">FM Rate/Kg (₹)</label><Input type="number" value={b2bOverrides.fm_rate_per_kg} onChange={e=>ub("fm_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">LM Rate/Kg (₹)</label><Input type="number" value={b2bOverrides.lm_rate_per_kg} onChange={e=>ub("lm_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">Midmile Rate/Kg (₹)</label><Input type="number" value={b2bOverrides.midmile_rate_per_kg} onChange={e=>ub("midmile_rate_per_kg",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">Fuel Surcharge (%)</label><Input type="number" value={b2bOverrides.fuel_surcharge_percent} onChange={e=>ub("fuel_surcharge_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">Min Chargeable Wt (kg)</label><Input type="number" value={b2bOverrides.min_chargeable_weight} onChange={e=>ub("min_chargeable_weight",parseFloat(e.target.value)||0)} className="mt-1"/></div>
            <div><label className="text-xs text-slate-600">Margin (%)</label><Input type="number" value={b2bOverrides.margin_percent} onChange={e=>ub("margin_percent",parseFloat(e.target.value)||0)} className="mt-1"/></div>
          </div>
          <InsuranceRadio value={b2bOverrides.insurance} onChange={v=>ub("insurance",v)} error={false} networkType="OWN" />
          <Button onClick={saveB2B} disabled={saving} className="bg-purple-600 hover:bg-purple-700"><Save className="w-4 h-4 mr-1"/>Save B2B Walk-in Rates</Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function RateAgreements() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [pageTab, setPageTab] = useState("individual");
  const [agreements, setAgreements] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [filterTab, setFilterTab] = useState("ALL");
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [approveId, setApproveId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [comment, setComment] = useState("");
  const [customers, setCustomers] = useState([]);

  const fetchAgreements = useCallback(async () => {
    const params = filterTab !== "ALL" ? `?status=${filterTab}` : "";
    const r = await fetch(`${API}/api/v2/rate-agreements${params}`, { headers });
    if (r.ok) setAgreements(await r.json());
  }, [filterTab]);

  const fetchPending = useCallback(async () => {
    const r = await fetch(`${API}/api/v2/rate-agreements?status=PENDING_APPROVAL`, { headers });
    if (r.ok) { const d = await r.json(); setPendingCount(d.length); }
  }, []);

  useEffect(() => { fetchAgreements(); }, [fetchAgreements]);
  useEffect(() => { fetchPending(); }, [fetchPending]);
  useEffect(() => {
    fetch(`${API}/api/users?role=customer`, { headers }).then(r=>r.json()).then(d => { if (Array.isArray(d)) setCustomers(d); });
  }, []);

  const submitForApproval = async (id) => {
    if (!window.confirm("Submit for approval?")) return;
    const r = await fetch(`${API}/api/v2/rate-agreements/${id}/submit`, { method:"POST", headers });
    if (r.ok) { toast.success("Submitted"); fetchAgreements(); fetchPending(); }
    else { const e = await r.json(); toast.error(e.detail||"Failed"); }
  };
  const approveAgreement = async () => {
    if (!comment.trim()) return toast.error("Comment required");
    const r = await fetch(`${API}/api/v2/rate-agreements/${approveId}/approve`, { method:"POST", headers, body: JSON.stringify({comment}) });
    if (r.ok) { toast.success("Approved"); setApproveId(null); setComment(""); fetchAgreements(); fetchPending(); }
    else { const e = await r.json(); toast.error(e.detail||"Failed"); }
  };
  const rejectAgreement = async () => {
    if (!comment.trim()) return toast.error("Reason required");
    const r = await fetch(`${API}/api/v2/rate-agreements/${rejectId}/reject`, { method:"POST", headers, body: JSON.stringify({comment}) });
    if (r.ok) { toast.success("Rejected"); setRejectId(null); setComment(""); fetchAgreements(); fetchPending(); }
    else { const e = await r.json(); toast.error(e.detail||"Failed"); }
  };
  const deleteAgreement = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    const r = await fetch(`${API}/api/v2/rate-agreements/${id}`, { method:"DELETE", headers });
    if (r.ok) { toast.success("Deleted"); fetchAgreements(); } else toast.error("Failed");
  };

  const filterTabs = ["ALL","ACTIVE","DRAFT","PENDING_APPROVAL","EXPIRED"];

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-5" data-testid="rate-agreements-page">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600"/>Rate Agreements</h1>
            <p className="text-xs text-slate-500 mt-0.5">Manage customer and partner rate agreements</p>
          </div>
          {pageTab==="individual" && (
            <Button onClick={()=>{setEditData(null);setFormOpen(true);}} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-agreement-btn">
              <Plus className="w-4 h-4 mr-1"/>New Agreement
            </Button>
          )}
        </div>

        {/* Page-level Tabs */}
        <div className="flex gap-1 border-b">
          <button onClick={()=>setPageTab("individual")} className={`px-4 py-2.5 text-sm font-bold transition-colors ${pageTab==="individual"?"border-b-2 border-indigo-600 text-indigo-600":"text-slate-500 hover:text-slate-700"}`} data-testid="page-tab-individual">Individual Agreements</button>
          <button onClick={()=>setPageTab("retail")} className={`px-4 py-2.5 text-sm font-bold transition-colors ${pageTab==="retail"?"border-b-2 border-indigo-600 text-indigo-600":"text-slate-500 hover:text-slate-700"}`} data-testid="page-tab-retail">Retail Rate Card</button>
        </div>

        {/* ─── TAB 1: Individual Agreements ─── */}
        {pageTab==="individual" && (<>
          {/* Pending Banner */}
          {pendingCount>0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="pending-banner">
              <span className="flex items-center gap-2 text-amber-800 text-sm font-medium"><AlertTriangle className="w-4 h-4"/>{pendingCount} agreement{pendingCount>1?"s":""} pending approval</span>
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={()=>setFilterTab("PENDING_APPROVAL")}>Review</Button>
            </div>
          )}
          {/* Filter Tabs */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="flex border-b">
              {filterTabs.map(t=>(
                <button key={t} onClick={()=>setFilterTab(t)} className={`px-4 py-2.5 text-xs font-bold transition-colors ${filterTab===t?"border-b-2 border-indigo-600 text-indigo-600":"text-slate-500 hover:text-slate-700"}`} data-testid={`filter-${t.toLowerCase()}`}>
                  {t==="PENDING_APPROVAL"?"Pending":t==="ALL"?"All":t.charAt(0)+t.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Customer Name</th>
                    <th className="px-3 py-2 text-left">Customer Type</th>
                    <th className="px-3 py-2">Network</th>
                    <th className="px-3 py-2">Service</th>
                    <th className="px-3 py-2">Order Type</th>
                    <th className="px-3 py-2">From</th>
                    <th className="px-3 py-2">To</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {agreements.length===0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">No agreements found</td></tr>}
                  {agreements.map(ag=>(
                    <tr key={ag.id} className="hover:bg-slate-50" data-testid={`row-${ag.id}`}>
                      <td className="px-3 py-2 font-medium">{ag.entity_name||ag.entity_id}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{ag.customer_type||ag.entity_type}</td>
                      <td className="px-3 py-2 text-center text-xs">{ag.network_type}</td>
                      <td className="px-3 py-2 text-center text-xs">{ag.service_type}</td>
                      <td className="px-3 py-2 text-center text-xs">{ag.order_type}</td>
                      <td className="px-3 py-2 text-center text-xs font-mono">{ag.valid_from}</td>
                      <td className="px-3 py-2 text-center text-xs font-mono">{ag.valid_to}</td>
                      <td className="px-3 py-2 text-center"><Badge className={`text-[10px] ${STATUS_STYLES[ag.status]||"bg-gray-100"}`}>{ag.status}</Badge></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-center">
                          {ag.status==="DRAFT" && (<>
                            <button onClick={()=>{setEditData(ag);setFormOpen(true);}} className="text-blue-600 hover:text-blue-800" title="Edit"><Edit className="w-3.5 h-3.5"/></button>
                            <button onClick={()=>submitForApproval(ag.id)} className="text-amber-600 hover:text-amber-800" title="Submit"><Send className="w-3.5 h-3.5"/></button>
                            <button onClick={()=>deleteAgreement(ag.id)} className="text-red-500 hover:text-red-700" title="Delete"><Trash2 className="w-3.5 h-3.5"/></button>
                          </>)}
                          {ag.status==="PENDING_APPROVAL" && (<>
                            <button onClick={()=>{setApproveId(ag.id);setComment("");}} className="text-green-600 hover:text-green-800" title="Approve"><CheckCircle2 className="w-3.5 h-3.5"/></button>
                            <button onClick={()=>{setRejectId(ag.id);setComment("");}} className="text-red-500 hover:text-red-700" title="Reject"><XCircle className="w-3.5 h-3.5"/></button>
                          </>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>)}

        {/* ─── TAB 2: Retail Rate Card ─── */}
        {pageTab==="retail" && <RetailRateCard headers={headers} />}

        {/* Full Screen Agreement Form */}
        <AgreementFormModal open={formOpen} onClose={()=>setFormOpen(false)} editData={editData} customers={customers} onSaved={()=>{fetchAgreements();fetchPending();}} headers={headers} />

        {/* Approve Modal */}
        <Dialog open={!!approveId} onOpenChange={()=>setApproveId(null)}>
          <DialogContent><DialogHeader><DialogTitle>Approve Agreement</DialogTitle></DialogHeader>
            <Textarea placeholder="Approval comment (required)" value={comment} onChange={e=>setComment(e.target.value)} rows={3} data-testid="approve-comment"/>
            <DialogFooter><Button variant="outline" onClick={()=>setApproveId(null)}>Cancel</Button><Button onClick={approveAgreement} className="bg-green-600 hover:bg-green-700" data-testid="confirm-approve">Approve</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Reject Modal */}
        <Dialog open={!!rejectId} onOpenChange={()=>setRejectId(null)}>
          <DialogContent><DialogHeader><DialogTitle>Reject Agreement</DialogTitle></DialogHeader>
            <Textarea placeholder="Reason for rejection (required)" value={comment} onChange={e=>setComment(e.target.value)} rows={3} data-testid="reject-comment"/>
            <DialogFooter><Button variant="outline" onClick={()=>setRejectId(null)}>Cancel</Button><Button onClick={rejectAgreement} variant="destructive" data-testid="confirm-reject">Reject</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
